

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewLoyaltySummaryModal from 'components/NewLoyaltySummaryModal';

const LoyaltySummaries = () => {
    const [summaries, setSummaries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('loyalty_summaries');

    useEffect(() => {
        fetchSummaries();
    }, []);

    const fetchSummaries = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/loyalty-summaries/');
            setSummaries(response.data);
        } catch (error) {
            console.error('Error fetching loyalty summaries:', error);
            setSnackbar({ open: true, message: 'Error loading loyalty summaries', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveSummary = async (payload) => {
        try {
            await axiosInstance.post('/loyalty-summaries/', payload);
            setSnackbar({ open: true, message: 'Loyalty Summary created successfully', severity: 'success' });
            fetchSummaries();
        } catch (error) {
            console.error('Error saving loyalty summary:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/loyalty-summary/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Customer",
            accessor: "customer_id",
            width: "20%",
            Cell: ({ row }) => row.original.customer?.name || row.original.customer_id
        },
        { Header: "Balance Points", accessor: "balance_points", width: "12%" },
        { Header: "Lifetime Earned", accessor: "lifetime_earned", width: "12%" },
        { Header: "Lifetime Redeemed", accessor: "lifetime_redeemed", width: "12%" },
        { Header: "Lifetime Expired", accessor: "lifetime_expired", width: "12%" },
        {
            Header: "Last Earned On",
            accessor: "last_earned_on",
            width: "12%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "5%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: summaries,
    }), [summaries]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Loyalty Summaries</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Summary
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : summaries.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Loyalty Summary" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewLoyaltySummaryModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveSummary}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default LoyaltySummaries;

